import { RoleNames, RouteNames } from '../constans/routes';
import { ITopic } from '../hooks/useSurveyContents';
import { showToast } from '../redux/slices/toast.slice';
import store from '../redux/store';
import { IGroupDistribution } from '../services/individualReport';
import { ECode, IPrmAnalysisResults, IRange } from '../services/reports';
import { ISurveyFilters } from '../services/surveys';

export const filterEmptyFilters = (filters: ISurveyFilters) => {
  return Object.keys(filters).reduce((finalFilters: ISurveyFilters, key: string) => {
    if (filters[key]) {
      return { ...finalFilters, [key]: filters[key] };
    }
    return finalFilters;
  }, {});
};

const { dispatch } = store;
export const genToastBody = async (title: string, desc: string) =>
  dispatch(
    showToast({
      title: title || 'Error',
      description: desc || 'Something went wrong',
      mode: 'error',
    })
  );

export const mergeGroupOfErrorsToString = (details: any): string => {
  const messages: string[] = [];

  const extractMessages = (value: any) => {
    if (typeof value === 'string') {
      messages.push(value);
    } else if (Array.isArray(value)) {
      value.forEach(extractMessages);
    } else if (typeof value === 'object' && value !== null) {
      Object.values(value).forEach(extractMessages);
    }
  };

  extractMessages(details);
  return messages.join(' ');
};

interface IErrorData {
  title: string;
  details: string;
}
export const getErrorDesc = (errorData: IErrorData): { title: string; description: string } => {
  if (errorData?.title) {
    if (errorData?.details && typeof errorData?.details === 'string') {
      return { title: errorData?.title, description: errorData?.details };
    }
    if (errorData?.details && typeof errorData?.details === 'object') {
      const description = mergeGroupOfErrorsToString(errorData?.details);
      return { title: errorData?.title, description };
    }
  }
  return { title: 'Error', description: 'Something went wrong' };
};

export const getLangCode = (lang: string) => {
  switch (lang) {
    case 'English':
    case 'Engelsk':
    case 'Engelska':
      return 'en';
    case 'Norwegian':
    case 'Norsk':
    case 'Norska':
      return 'nb';
    case 'Swedish':
    case 'Svensk':
    case 'Svenska':
      return 'sv';
    default:
      return 'en';
  }
};

export const getLang = (langCode: string) => {
  switch (langCode) {
    case 'en':
      return 'English';
    case 'nb':
      return 'Norwegian';
    case 'sv':
      return 'Swedish';
    default:
      return 'English';
  }
};

export const countArrayInArrayLength = (arr: ITopic[]) => {
  return arr.reduce((res: number, { items }: ITopic) => {
    const numberOfQuestions = items.length;
    return res + numberOfQuestions;
  }, 0);
};

export const objectToString = (obj: any) => {
  const stringObject = Object.keys(obj).reduce((res: string, key: string) => {
    return `${res}${key}:${obj[key]},`;
  }, '');
  const finalString = stringObject.slice(0, -1);
  return finalString;
};

interface IStringifyAbleObject {
  [key: string]: string[];
}

interface IStringifyResult {
  [key: string]: string;
}
export const stringifyArraysInObject = (obj: IStringifyAbleObject): IStringifyResult => {
  return Object.keys(obj).reduce((res: any, key: string) => {
    const value = obj[key].join('');
    return { ...res, [key]: value };
  }, {});
};

export const getInitialRouteFromRole = (role: string) => {
  switch (role) {
    case RoleNames.EBHRConsultant:
      return RouteNames.Surveys;
    case RoleNames.SurveyAdministrator:
      return RouteNames.Surveys;
    case RoleNames.SurveyConsultant:
      return RouteNames.Surveys;
    case RoleNames.Manager:
      return RouteNames.Reports;
    default:
      return RouteNames.Surveys;
  }
};

export const getScoreSortFilters = (
  currentFilters: { [key: string]: any },
  parentKey: string,
  childrenKey: string
) => {
  const parentObject = currentFilters[parentKey];
  if (typeof parentObject === 'string') {
    const decodedParentBody = JSON.parse(parentObject);
    const child = decodedParentBody[childrenKey];
    if (child?.sortOrder) {
      const orderArr = child?.sortOrder.split(':');
      const value = orderArr[1] || 'default';
      return { key: 'score', value };
    }
    return { key: 'score', value: null };
  }
  return { key: 'score', value: null };
};

export const transformFormErrors = (details: { [key: string]: string }) => {
  return Object.keys(details).reduce((res: { [key: string]: string }, key: string) => {
    const errorObj = { [key]: details[key][0] };
    return { ...res, ...errorObj };
  }, {});
};

export const getLocaleFormatDate = (date: string | number | Date, language: string) =>
  new Date(date).toLocaleDateString(language, { month: 'short', year: 'numeric' });

type TStorageType = 'local' | 'session';

const setStorageType = (type: TStorageType) =>
  type === 'local' ? window.localStorage : window.sessionStorage;

export const getStorageItem = <T extends unknown>(key: string, type: TStorageType = 'local') => {
  try {
    const storage = setStorageType(type);
    const item = storage.getItem(key);

    return item ? (JSON.parse(item) as T) : undefined;
  } catch (error) {
    return null;
  }
};

export const setStorageItem = <T extends unknown>(
  key: string,
  value: T,
  type: TStorageType = 'local'
) => {
  try {
    const storage = setStorageType(type);

    return storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    return null;
  }
};

export const clearStorageItem = (key: string, type: TStorageType = 'local') => {
  try {
    const storage = setStorageType(type);

    return storage.removeItem(key);
  } catch (error) {
    return null;
  }
};

export const orderGroupDistribution = (group_distribution: IGroupDistribution) =>
  Object.entries(group_distribution).reduce<number[]>((acc, [key, value]) => {
    const indexes: Record<keyof IGroupDistribution, number> = {
      weak_percentage: 0,
      normal_percentage: 1,
      strong_percentage: 2,
    };

    acc[indexes[key as keyof IGroupDistribution]] = value;

    return acc;
  }, []);

export const convertTypeString = (type: string): string =>
  type
    .split(' ')
    .slice(1)
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('');

export const LOW_SCORE = 4.0;
export const MEDIUM_SCORE = 4.6;
export const HIGH_SCORE = 5.5;

export const defaultScales = [1, 2, 3, 4, 5, 6, 7];

export const getPRMBarChartData = (
  survey: string,
  prm: IPrmAnalysisResults,
  { lng }: { lng: string }
) => {
  const { historicalBenchmarks, segmentResults } = prm;

  const surveys = historicalBenchmarks.reduce((acc, { survey, prmAnalysisResults }, index) => {
    const { endDate, type, numResponses } = survey;

    prmAnalysisResults.forEach(({ code, share, count }) => {
      if (!acc[code]) {
        acc[code] = [];
      }

      acc[code][index] = {
        name: `${new Date(endDate).toLocaleDateString(lng, {
          month: 'short',
          year: 'numeric',
        })} ${type}`,
        share,
        numResponses: count,
      };
    });

    return acc;
  }, {} as Record<ECode, { name: string; share: number; numResponses: number }[]>);

  const chart = segmentResults.map(({ segment, share, count }) => {
    const { current: numResponses } = count;
    const { current } = share;
    const { code, title } = segment;

    return {
      code,
      group: title,
      surveys: [
        { name: survey, share: current, numResponses: numResponses },
        ...(surveys[code] || []),
      ],
    };
  });

  const reference = segmentResults.reduce((acc, { share, segment }) => {
    const { expectedRange } = share;
    const { code } = segment;

    return {
      ...acc,
      [code]: expectedRange,
    };
  }, {} as Record<ECode, IRange>);

  return { chart, reference };
};

interface IIntroductionSection {
  title: string;
  text: string;
}

export const parseIndividualReportIntroduction = (text: string) => {
  const lines = text.split(/<br>|<\/p>/);

  const parsedArray: IIntroductionSection[] = [];
  let currentSection = { title: '', text: '' };

  lines.forEach(line => {
    line = line.trim();

    if (line.startsWith('<b>') && line.endsWith('</b>')) {
      if (currentSection.text) {
        parsedArray.push({ ...currentSection });
      }

      currentSection = {
        title: line.replace('<b>', '').replace('</b>', '').trim(),
        text: '',
      };
    } else if (line.startsWith('<p>') && line.length > 0) {
      const paragraph = line.replace('<p>', '').trim();

      if (paragraph) {
        currentSection.text += (currentSection.text ? '\n\n' : '') + paragraph;
      }
    } else if (line.length > 0) {
      currentSection.text += (currentSection.text ? '\n' : '') + line;
    }
  });

  if (currentSection.text) {
    parsedArray.push({ ...currentSection });
  }

  return parsedArray;
};

interface IKeytakeawaysSection {
  list?: string[];
  title: string;
  text: string;
}

export const parseIndividualReportKeytakeaways = (text: string) => {
  const sections: IKeytakeawaysSection[] = [];
  const rawSections = text.split('<b>').slice(1);

  rawSections.forEach(section => {
    const titleEndIndex = section.indexOf('</b>');
    const title = section.substring(0, titleEndIndex);
    let content = section.substring(titleEndIndex + 4).trim();

    if (content.includes('<ul>')) {
      const listStartIndex = content.indexOf('<ul>');
      const listEndIndex = content.indexOf('</ul>') + 5;
      const text = content.substring(0, listStartIndex).replace(/<br>/g, '').trim();
      const rawList = content.substring(listStartIndex, listEndIndex);
      const listItems = rawList
        .split('<li>')
        .slice(1)
        .map(item => item.replace(/<\/li>|<\/ul>/g, '').trim());

      sections.push({ title, text, list: listItems });
    } else {
      content = content.replace(/<br>/g, '').trim();
      sections.push({ title, text: content });
    }
  });

  return sections;
};
